import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "@/i18n";
import * as cognito from "@/services/cognito";
import { CognitoError } from "@/types/auth";

/**
 * Onboarding (docs/SPEC.md 3.1): e-mail + phone (E.164), each confirmed with a
 * Cognito code, required before the student/teacher can use anything else.
 */
export function Onboarding() {
  const { t } = useTranslation();
  const { getAccessToken, refreshMe } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [step, setStep] = useState<"contact" | "confirm">("contact");
  const [emailCode, setEmailCode] = useState("");
  const [phoneCode, setPhoneCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const sendCodes = async () => {
    const token = getAccessToken();
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      await cognito.updateUserAttributes(token, { email, phone_number: phone });
      setStep("confirm");
    } catch (err) {
      const code = err instanceof CognitoError ? err.code : "generic";
      setError(t(`auth.error.${code}`, t("auth.error.generic", "Anmeldung fehlgeschlagen. Bitte erneut versuchen.")));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    const token = getAccessToken();
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      if (emailCode) await cognito.verifyUserAttribute(token, "email", emailCode);
      if (phoneCode) await cognito.verifyUserAttribute(token, "phone_number", phoneCode);
      await refreshMe();
      navigate("/", { replace: true });
    } catch (err) {
      const code = err instanceof CognitoError ? err.code : "generic";
      setError(t(`auth.error.${code}`, t("auth.error.generic", "Anmeldung fehlgeschlagen. Bitte erneut versuchen.")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-sm space-y-4 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900">{t("onboarding.title", "Kontaktdaten bestätigen")}</h2>
        {error && <p className="rounded-lg bg-red-50 p-2 text-sm text-red-700">{error}</p>}

        {step === "contact" ? (
          <>
            <input
              type="email"
              placeholder={t("onboarding.email", "E-Mail-Adresse")}
              className="tap-target w-full rounded-lg border border-gray-300 px-3 text-base"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
            <input
              type="tel"
              placeholder="+49…"
              aria-label={t("onboarding.phone", "Telefonnummer")}
              className="tap-target w-full rounded-lg border border-gray-300 px-3 text-base"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              required
            />
            <button disabled={busy || !email || !phone} onClick={() => void sendCodes()} className="tap-target flex w-full items-center justify-center gap-2 rounded-lg bg-brand-green py-3 text-sm font-medium text-brand-ink disabled:opacity-50">
              {busy && <Loader2 size={18} className="animate-spin" aria-hidden />}
              {t("onboarding.sendCode", "Code senden")}
            </button>
          </>
        ) : (
          <>
            <input
              placeholder={`${t("onboarding.code", "Bestätigungscode")} (E-Mail)`}
              className="tap-target w-full rounded-lg border border-gray-300 px-3 text-base"
              value={emailCode}
              onChange={(event) => setEmailCode(event.target.value)}
            />
            <input
              placeholder={`${t("onboarding.code", "Bestätigungscode")} (Telefon)`}
              className="tap-target w-full rounded-lg border border-gray-300 px-3 text-base"
              value={phoneCode}
              onChange={(event) => setPhoneCode(event.target.value)}
            />
            <button disabled={busy} onClick={() => void confirm()} className="tap-target flex w-full items-center justify-center gap-2 rounded-lg bg-brand-green py-3 text-sm font-medium text-brand-ink disabled:opacity-50">
              {busy && <Loader2 size={18} className="animate-spin" aria-hidden />}
              {t("onboarding.confirm", "Bestätigen")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
